/**
 * ============================================================================
 * DELIVERY HISTORY PAGE - 100% Ready
 * ============================================================================
 * 
 * Page for viewing delivery history and tracking
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTransaction } from '../hooks/useTransaction';
import { useToast } from '../providers';
import { useI18n } from '../providers';
import { UserDashboardLayout } from '../components/user';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Select from '../components/common/Select';
import Input from '../components/common/Input';
import Spinner from '../components/common/Spinner';
import EmptyState from '../components/common/EmptyState';
import StatusBadge from '../components/common/StatusBadge';
import Tabs from '../components/common/Tabs';
import DeliveryTracking from '../components/delivery/DeliveryTracking';
import DeliveryStatus from '../components/delivery/DeliveryStatus';
import '../styles/animations.css';
import '../styles/global.css';

/**
 * Delivery History Page Component
 * @returns {JSX.Element}
 */
const DeliveryHistory = () => {
  const navigate = useNavigate();
  const { listTransactions, loading } = useTransaction();
  const { showError } = useToast();
  const { formatCurrency, formatDate, language } = useI18n();

  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    loadTransactions();
  }, [filter, page]);

  const loadTransactions = async () => {
    try {
      setIsLoading(true);
      const data = await listTransactions({
        page,
        limit: 20,
        status: filter !== 'all' ? filter : undefined,
        search: searchQuery || undefined,
      });

      const transactionsData = Array.isArray(data) ? data : data?.transactions || [];
      setTransactions(transactionsData);
      setHasMore(transactionsData.length === 20);
    } catch (error) {
      showError(language === 'fr' ? 'Échec du chargement' : 'Failed to load transactions');
      console.error('Load transactions error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadTransactions();
  };

  const getDeliveryStatus = (transaction) => {
    if (transaction.status === 'completed') {
      return { status: 'delivered', label: language === 'fr' ? 'Livré' : 'Delivered', color: 'success' };
    }
    if (transaction.status === 'delivered') {
      return { status: 'pending_approval', label: language === 'fr' ? 'En attente d\'approbation' : 'Pending Approval', color: 'warning' };
    }
    if (transaction.deliveredAt) {
      return { status: 'delivered', label: language === 'fr' ? 'Livré' : 'Delivered', color: 'success' };
    }
    if (transaction.status === 'in_progress' || transaction.status === 'escrow_funded') {
      return { status: 'in_progress', label: language === 'fr' ? 'En cours' : 'In Progress', color: 'primary' };
    }
    return { status: 'not_started', label: language === 'fr' ? 'Non commencé' : 'Not Started', color: 'default' };
  };

  const filterTabs = [
    { id: 'all', label: language === 'fr' ? 'Tous' : 'All' },
    { id: 'in_progress', label: language === 'fr' ? 'En cours' : 'In Progress' },
    { id: 'delivered', label: language === 'fr' ? 'Livré' : 'Delivered' },
    { id: 'completed', label: language === 'fr' ? 'Complété' : 'Completed' },
  ];

  const statusOptions = [
    { value: 'all', label: language === 'fr' ? 'Tous les statuts' : 'All Statuses' },
    { value: 'in_progress', label: language === 'fr' ? 'En cours' : 'In Progress' },
    { value: 'delivered', label: language === 'fr' ? 'Livré' : 'Delivered' },
    { value: 'completed', label: language === 'fr' ? 'Complété' : 'Completed' },
  ];

  // Filter transactions that have delivery-related status
  const deliveryTransactions = transactions.filter((t) => {
    if (filter === 'all') return true;
    const deliveryStatus = getDeliveryStatus(t);
    return deliveryStatus.status === filter || t.status === filter;
  });

  return (
    <UserDashboardLayout>
      {/* Header */}
      <div className="mb-8 animate-fadeInUp">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-4xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
              📦 {language === 'fr' ? 'Historique de livraison' : 'Delivery History'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 text-lg">
              {language === 'fr'
                ? 'Suivez toutes vos livraisons et transactions'
                : 'Track all your deliveries and transactions'}
            </p>
          </div>
        </div>
      </div>

      {/* Delivery Analytics */}
      {deliveryTransactions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 animate-fadeInUp" style={{ animationDelay: '0.05s' }}>
          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-blue-50/80 to-cyan-50/80 dark:from-blue-900/20 dark:to-cyan-900/20"
            hover
          >
            <div className="text-center">
              <div className="text-2xl font-black bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-1">
                {deliveryTransactions.length}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {language === 'fr' ? 'Total Livraisons' : 'Total Deliveries'}
              </div>
            </div>
          </Card>
          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-green-50/80 to-emerald-50/80 dark:from-green-900/20 dark:to-emerald-900/20"
            hover
          >
            <div className="text-center">
              <div className="text-2xl font-black bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent mb-1">
                {deliveryTransactions.filter((t) => t.status === 'completed' || t.status === 'delivered').length}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {language === 'fr' ? 'Complétées' : 'Completed'}
              </div>
            </div>
          </Card>
          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-yellow-50/80 to-orange-50/80 dark:from-yellow-900/20 dark:to-orange-900/20"
            hover
          >
            <div className="text-center">
              <div className="text-2xl font-black bg-gradient-to-r from-yellow-600 to-orange-600 bg-clip-text text-transparent mb-1">
                {deliveryTransactions.filter((t) => t.status === 'delivered' || getDeliveryStatus(t).status === 'pending_approval').length}
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {language === 'fr' ? 'En Attente' : 'Pending'}
              </div>
            </div>
          </Card>
          <Card
            variant="glass"
            className="backdrop-blur-xl border-white/20 dark:border-white/10 bg-gradient-to-br from-purple-50/80 to-pink-50/80 dark:from-purple-900/20 dark:to-pink-900/20"
            hover
          >
            <div className="text-center">
              <div className="text-2xl font-black bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-1">
                {deliveryTransactions.length > 0
                  ? Math.round(
                      (deliveryTransactions.filter((t) => t.status === 'completed').length /
                        deliveryTransactions.length) *
                        100
                    )
                  : 0}
                %
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-400">
                {language === 'fr' ? 'Taux de Réussite' : 'Success Rate'}
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={language === 'fr' ? 'Rechercher des transactions...' : 'Search transactions...'}
              className="w-full"
            />
          </div>
          <div className="sm:w-48">
            <Select
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPage(1);
              }}
              options={statusOptions}
            />
          </div>
          <Button type="submit" variant="primary">
            {language === 'fr' ? 'Rechercher' : 'Search'}
          </Button>
          {(searchQuery || filter !== 'all') && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearchQuery('');
                setFilter('all');
                setPage(1);
              }}
            >
              {language === 'fr' ? 'Effacer' : 'Clear'}
            </Button>
          )}
        </form>
      </Card>

      {/* Filter Tabs */}
      <Card variant="glass" className="mb-6 animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
        <Tabs
          tabs={filterTabs}
          activeTab={filter}
          onTabChange={(tabId) => {
            setFilter(tabId);
            setPage(1);
          }}
          variant="pills"
        />
      </Card>

      {/* Transactions List */}
      {isLoading ? (
        <Card variant="glass" className="p-12">
          <div className="flex items-center justify-center">
            <Spinner size="lg" />
          </div>
        </Card>
      ) : deliveryTransactions.length === 0 ? (
        <Card variant="glass" className="p-12 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
          <EmptyState
            icon="📦"
            title={language === 'fr' ? 'Aucune livraison' : 'No deliveries'}
            description={
              language === 'fr'
                ? 'Vous n\'avez pas encore de transactions avec livraison'
                : "You don't have any transactions with delivery yet"
            }
          />
        </Card>
      ) : (
        <div className="space-y-4 animate-fadeInUp" style={{ animationDelay: '0.3s' }}>
          {deliveryTransactions.map((transaction, index) => {
            const deliveryStatus = getDeliveryStatus(transaction);
            return (
              <Card
                key={transaction.id || index}
                variant="glass"
                hover
                clickable
                onClick={() => navigate(`/transactions/${transaction.id}`)}
                className="animate-fadeInUp"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4 flex-1">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center text-2xl shadow-lg">
                      📦
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-black text-gray-900 dark:text-white">
                          {transaction.title || (language === 'fr' ? 'Transaction' : 'Transaction')}
                        </h3>
                        <StatusBadge
                          variant={deliveryStatus.color}
                          size="sm"
                        >
                          {deliveryStatus.label}
                        </StatusBadge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mb-2">
                        <span className="font-medium">
                          {formatCurrency(transaction.amount || 0, transaction.currency || 'XAF')}
                        </span>
                        {transaction.deliveryDeadline && (
                          <>
                            <span>•</span>
                            <span>
                              {language === 'fr' ? 'Échéance:' : 'Deadline:'} {formatDate(transaction.deliveryDeadline)}
                            </span>
                          </>
                        )}
                        {transaction.deliveredAt && (
                          <>
                            <span>•</span>
                            <span>
                              {language === 'fr' ? 'Livré le' : 'Delivered'} {formatDate(transaction.deliveredAt)}
                            </span>
                          </>
                        )}
                      </div>
                      {/* Delivery Tracking */}
                      {transaction.deliveredAt && (
                        <div className="mt-3">
                          <DeliveryTracking transaction={transaction} compact />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (transaction.status === 'delivered') {
                          navigate(`/transactions/${transaction.id}/approve`);
                        } else if (transaction.status === 'in_progress' || transaction.status === 'escrow_funded') {
                          navigate(`/transactions/${transaction.id}/deliver`);
                        } else {
                          navigate(`/transactions/${transaction.id}`);
                        }
                      }}
                    >
                      {transaction.status === 'delivered'
                        ? language === 'fr' ? 'Approuver' : 'Approve'
                        : transaction.status === 'in_progress' || transaction.status === 'escrow_funded'
                        ? language === 'fr' ? 'Marquer livré' : 'Mark Delivered'
                        : language === 'fr' ? 'Voir' : 'View'}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Load More */}
      {hasMore && (
        <div className="mt-6 text-center animate-fadeInUp" style={{ animationDelay: '0.4s' }}>
          <Button
            variant="outline"
            onClick={() => setPage((prev) => prev + 1)}
            loading={loading}
          >
            {language === 'fr' ? 'Charger plus' : 'Load More'}
          </Button>
        </div>
      )}
    </UserDashboardLayout>
  );
};

export default DeliveryHistory;
